import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { TextInput, Bucket, BucketHeader, SelectInput, Checkbox, NumericInput } from "~/core";

import * as actions from "./actions";
import * as selectors from "./selectors";
import { getSetValuesForErrorCodeList } from "~/action-panel/components/common/validation-utils";
import { messages } from "../i18n-messages";

import { AnalysisIcon } from "./icons/analysis";
import "~/action-panel/components/common/rec-event-info/rec-event-info.css";
import "./analysis-info.css";
import { eventListSelectors } from "../../../event-module";
import { eventsSelectors } from "~/recs-events";
import { IAnalysisNormaliedYieldSummary } from "~/recs-events/analysis/model";
import { AgEventSummary } from "~/recs-events/events/model";
import { FormattingHelpers, LayerAPI, PicklistAPI } from "@ai360/core";
import { config as intlConfig } from "~/intl-provider/config";
import { NORMALIZED_YIELD, NORMALIZED_YIELD_BATCH, NORMALIZED_YIELD_BENCHMARK } from "./interfaces";

export const formLabelMessage = messages.analysisFormSideBarText;
export const formLabelIcon = AnalysisIcon;

const errorCodeToMessageIdSetMap = new Map([
    [2805, messages.analysisMethodPlaceholderText], // ErrorCode.AnalysisLayerNameIsRequired
    [2800, messages.analysisMethodNoYieldLayers], // ErrorCode.AnalysisNoYieldDataExists
    [2806, messages.analysisDuplicateName],
]);

export const errorCodesApply = (errorCodeList) => {
    return errorCodeList.some((errorCode) => errorCodeToMessageIdSetMap.has(errorCode));
};

class AnalysisNormalizedYieldFormProps {
    analysisSummary: IAnalysisNormaliedYieldSummary;
    intl: intlShape;
    normalizedEventYieldOptions: LayerAPI.INormalizedYieldSeason[];
    normalizedYieldType: string;
    pickListNumClasses: PicklistAPI.IPicklistItem[];
    analysisDetailsErrorCodeList: number[];
    onUpdateCurrentAnalysisSummary: (props: Partial<IAnalysisNormaliedYieldSummary>) => void;
    selectedEventGuidSet: Set<string>;
    selectedEventSummary: Map<string, AgEventSummary>;
}

const NormalizedYieldForm = (props: AnalysisNormalizedYieldFormProps): JSX.Element => {
    const {
        analysisSummary,
        normalizedEventYieldOptions,
        pickListNumClasses,
        analysisDetailsErrorCodeList,
        onUpdateCurrentAnalysisSummary,
        normalizedYieldType,
        selectedEventGuidSet,
        selectedEventSummary,
    } = props;
    const { formatMessage, formatNumber } = props.intl;

    //state variables
    const [errorMessagePlaceholderSet, setErrorMessagePlaceholderSet] = useState<Set<any>>(
        new Set()
    );

    useEffect(() => {
        if (!analysisDetailsErrorCodeList) {
            return;
        }

        const newErrorMessagePlaceholderSet = _getErrorMessagePlaceholderSet();
        setErrorMessagePlaceholderSet(newErrorMessagePlaceholderSet);
    }, [analysisDetailsErrorCodeList]);

    const _getAnalysisErrors = (errorMessagePlaceholderSet, formatMessage) => {
        if (errorMessagePlaceholderSet == null || errorMessagePlaceholderSet.size === 0) {
            return null;
        }
        const errorMessages = [];
        errorMessagePlaceholderSet.forEach((errorEntry) => {
            errorMessages.push(
                <div className="analysis-error-message">{formatMessage(errorEntry)}</div>
            );
        });
        return errorMessages;
    };

    const _getErrorMessagePlaceholderSet = () => {
        return getSetValuesForErrorCodeList(
            analysisDetailsErrorCodeList,
            errorCodeToMessageIdSetMap
        );
    };

    const _updateAnalysisToggleEvent = (guid, val) => {
        const { agEventGeneralGuidList } = analysisSummary;
        const list = agEventGeneralGuidList.filter(function (item) {
            return item !== guid;
        });
        if (val) {
            list.push(guid);
        }
        _updateAnalysisSummary({ agEventGeneralGuidList: list });
    };

    const _updateAnalysisSummary = (newProps) => {
        onUpdateCurrentAnalysisSummary(newProps);
    };

    const updateCropAverageYield = (
        cropGuid: string,
        importAttributeGuid: string,
        newBenchmarkYield: string
    ) => {
        const targetCropIndex = analysisSummary.cropList.findIndex(
            (c) => c.cropGuid === cropGuid && c.importAttributeGuid === importAttributeGuid
        );
        const hasChanged =
            targetCropIndex || targetCropIndex === 0
                ? analysisSummary.cropList[targetCropIndex].benchmarkYield !==
                  Number(newBenchmarkYield)
                : false;
        if (!hasChanged) {
            return;
        }
        const updatedCropList = [...analysisSummary.cropList];
        updatedCropList.splice(targetCropIndex, 1, {
            ...analysisSummary.cropList[targetCropIndex],
            benchmarkYield: newBenchmarkYield ? Number(newBenchmarkYield) : null,
        });
        _updateAnalysisSummary({ cropList: updatedCropList });
    };

    const _getYieldOptionLabel = (event) => {
        if (event.calculatedArea) {
            return `${event.displayName} - ${formatMessage(messages.analysisLayerCalculatedArea, {
                calculatedArea: event.calculatedArea,
            })}`;
        }
        return `${event.displayName}`;
    };

    const _removeCottonDuplicates = (events) => {
        return events.reduce((acc, evt) => {
            const duplicateEvtIdx = acc.findIndex(
                (a) => a.agEventGeneralGuid === evt.agEventGeneralGuid
            );
            if (events[duplicateEvtIdx]) {
                if (evt.displayName.indexOf("Cotton") > -1 && evt.attributeName === "Lint Yield") {
                    acc.splice(duplicateEvtIdx, 1, evt);
                }
            } else {
                acc.push(evt);
            }
            return acc;
        }, []);
    };

    const _renderPostLabel = (event) => {
        const formatOptions = intlConfig.customFormatOptions(
            FormattingHelpers.progressiveRoundingDecimals(Number(event.avgYield))
        );
        return (
            <div className="analysis-yield-label-cont">
                <div className="checkbox-label-text-span">{_getYieldOptionLabel(event)}</div>
                <div className="analysis-yield-label">
                    {event.attributeName} - {formatNumber(event.avgYield, formatOptions)}{" "}
                    {event.yieldUnit}
                </div>
            </div>
        );
    };

    const _checkHarvestEvent = (event) => {
        return event.agEventTypeName === "Harvest" && event.isImportedYn;
    };

    const getCropLine = (crop: LayerAPI.INormalizedYieldCrop, cropLabel: string, index: number) => {
        return (
            <div key={`crop-${index}`} className="input-row">
                <div className="crop-information">
                    <div className="crop-title" title={cropLabel}>
                        {`${cropLabel}:`}
                    </div>
                </div>
                <div className="yield-information">
                    <NumericInput
                        containerClassNames={["crop-benchmark-input"]}
                        scale={2}
                        placeholderText={formatMessage(messages.benchmarkYield)}
                        precision={11}
                        onChange={(value) =>
                            updateCropAverageYield(crop.cropGuid, crop.importAttributeGuid, value)
                        }
                        value={crop.benchmarkYield}
                        showTopLabel={true}
                        required={true}
                        testId={`analysis-normalized-yield-benchmark-yield-input-${index}`}
                    />
                </div>
            </div>
        );
    };

    const _getSelectedSeasons = (): number[] => {
        if (!selectedEventGuidSet || selectedEventGuidSet.size === 0) {
            return analysisSummary.croppingSeasonNameList;
        }
        const seasons = [];
        for (const eventGeneralGuid of selectedEventGuidSet) {
            const event = selectedEventSummary.get(eventGeneralGuid);
            if (_checkHarvestEvent(event)) {
                seasons.push(event?.croppingSeasonNum);
            }
        }
        return seasons;
    };

    const _getSelectedEventCount = () => {
        return analysisSummary?.agEventGeneralGuidList &&
            analysisSummary.agEventGeneralGuidList.length > 0
            ? new Set(analysisSummary.agEventGeneralGuidList).size
            : selectedEventGuidSet && selectedEventGuidSet.size > 0
            ? selectedEventGuidSet.size
            : 0;
    };

    const renderEventCountAndSeasons = () => {
        const selectedSeasons = _getSelectedSeasons();
        return (
            <div>
                <div className="event-count">
                    <p className="event-label">{formatMessage(messages.harvestEvents)} </p>
                    <p className="event-list">{_getSelectedEventCount()}</p>
                </div>
                <div className="seasons">
                    <p className="seasons-label">{formatMessage(messages.seasons)} </p>
                    <p className="seasons-list">
                        {Array.from([...new Set(selectedSeasons)]).join(", ")}
                    </p>
                </div>
            </div>
        );
    };

    const renderCropList = () => {
        const cropListSorted = analysisSummary.cropList
            ?.map((crop) => ({
                crop,
                label: crop.unitName ? `${crop.cropName} (${crop.unitName})` : crop.cropName,
            }))
            .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
        const cropList = cropListSorted?.map(({ crop, label }, i) => getCropLine(crop, label, i));
        return (
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="analysis-normalized-yield-benchmark-yield-bucket"
            >
                <BucketHeader className="analysis-info-bucket-header">
                    {formatMessage(messages.benchmarkYield)}
                </BucketHeader>
                {cropList.length > 0 ? <div className="crops">{cropList}</div> : null}
            </Bucket>
        );
    };

    const agEventGeneralGuidList =
        normalizedYieldType === NORMALIZED_YIELD_BATCH
            ? Array.from(selectedEventGuidSet)
            : analysisSummary.agEventGeneralGuidList;
    let eventCheckboxes;
    if (normalizedYieldType === NORMALIZED_YIELD) {
        eventCheckboxes = _removeCottonDuplicates(normalizedEventYieldOptions).map((event, key) => {
            const checked = agEventGeneralGuidList.some(function (guid) {
                return guid === event.agEventGeneralGuid;
            });
            return (
                <Checkbox
                    className="analysis-input"
                    key={key}
                    value={checked}
                    onChange={(evt, val) =>
                        _updateAnalysisToggleEvent(event.agEventGeneralGuid, val)
                    }
                    renderLabel={() => _renderPostLabel(event)}
                    testId={`analysis-normalized-yield-event-checkbox-${key}`}
                />
            );
        });
    }

    const analysisErrors = _getAnalysisErrors(errorMessagePlaceholderSet, formatMessage);

    return (
        <div className="rec-event-info-form">
            {analysisErrors}
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="analysis-normalized-yield-general-bucket"
            >
                <BucketHeader>{formatMessage(messages.analysisFormLabelText)}</BucketHeader>
                <div className="input-row half">
                    <TextInput
                        containerClassNames={[
                            {
                                "form-input-error": errorMessagePlaceholderSet.has(
                                    messages.analysisMethodPlaceholderText
                                ),
                            },
                        ]}
                        onChange={(v) => _updateAnalysisSummary({ name: v })}
                        placeholderText={formatMessage(messages.analysisNamePlaceholderText)}
                        required={true}
                        value={analysisSummary.name}
                        maxLength={50}
                        testId="analysis-normalized-yield-name-input"
                    />
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="analysis-normalized-yield-symbology-bucket"
            >
                <BucketHeader>
                    {formatMessage(messages.analysisSymbologyInfoPlaceholderText)}
                </BucketHeader>
                <div className="input-row half">
                    <SelectInput
                        clearable={false}
                        onChange={(v) => _updateAnalysisSummary({ numClasses: v })}
                        options={pickListNumClasses}
                        placeholderText={formatMessage(messages.analysisNumClassesPlaceholderText)}
                        required={true}
                        value={analysisSummary.numClasses}
                        testId="analysis-normalized-yield-num-classes-select"
                    />
                </div>
            </Bucket>
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="analysis-normalized-yield-events-bucket"
            >
                <BucketHeader className="analysis-info-bucket-header">
                    {formatMessage(messages.analysisEventsPlaceholderText)}
                </BucketHeader>
                {normalizedYieldType === NORMALIZED_YIELD ? (
                    eventCheckboxes
                ) : (
                    <div>{renderEventCountAndSeasons()}</div>
                )}
            </Bucket>
            {normalizedYieldType !== NORMALIZED_YIELD_BENCHMARK ||
            analysisSummary.cropList?.length < 1
                ? null
                : renderCropList()}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    onUpdateCurrentAnalysisSummary: (newProps) =>
        dispatch(actions.updateNormalizedYieldAnalysisSummary(newProps)),
});

const mapStateToProps = (state) => {
    const normalizedEventYieldOptions = selectors.getNormalizedYieldOptions(state);
    const normalizedYieldType = selectors.getNormalizedYieldType(state);
    const selectedEventGuidSet = eventListSelectors.getSelectedEventGuidSet(state);
    const selectedEventSummary = eventsSelectors.getEventGeneralGuidToEventSummaryMap(state);
    return {
        normalizedEventYieldOptions,
        normalizedYieldType,
        selectedEventSummary,
        selectedEventGuidSet,
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null)(injectIntl(NormalizedYieldForm));
